import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, PlusCircle, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_ROLES, STATUS_META } from "@/lib/constants";
import api from "@/lib/apiClient";

export default function Claims() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const isSP = user?.role === "service_provider";

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const params = st && st !== "all" ? { status: st } : {};
      const res = await api.get("/claims", { params });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  return (
    <div className="space-y-6" data-testid="claims-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{isSP ? "Assigned Services" : "Warranty Claims"}</h1>
          <p className="text-muted-foreground mt-1">{isSP ? "Services assigned to your center." : "All warranty claim requests and their resolution status."}</p>
        </div>
        {!isSP && (
          <Button onClick={() => navigate("/claims/new")} className="gap-2" data-testid="new-claim-cta">
            <PlusCircle className="h-4 w-4" /> New Claim
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.keys(STATUS_META).map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Claim No.</TableHead>
              <TableHead>Battery</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Issue</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>TAT</TableHead>
              <TableHead>Raised</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />No claims found.
              </TableCell></TableRow>
            ) : (
              rows.map((c) => (
                <TableRow key={c.id} onClick={() => navigate(`/claims/${c.id}`)} className="cursor-pointer hover:bg-accent/40" data-testid={`claim-row-${c.claim_no}`}>
                  <TableCell className="font-mono font-semibold text-primary">{c.claim_no}</TableCell>
                  <TableCell className="font-mono">{c.battery_serial}<div className="text-xs text-muted-foreground">{c.battery_model}</div></TableCell>
                  <TableCell>{c.customer_name}<div className="text-xs text-muted-foreground">{c.customer_type}</div></TableCell>
                  <TableCell className="max-w-[160px] truncate">{c.issue_type}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={c.status} />
                      {c.tat_breached && <span title="TAT breached" className="h-2 w-2 rounded-full bg-red-500 animate-pulse" data-testid="tat-breach-dot" />}
                    </div>
                  </TableCell>
                  <TableCell>{c.tat_days ? `${c.tat_days}d` : "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
